import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTokenFromLink } from '../hooks/useTokenFromLink';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * /admin/reset-password — public page reached from the staff (DoorlyDesk)
 * password-reset email. Reads the token from the link, strips it from the URL, posts to
 * /api/admin/reset-password, redirects to /admin/login on success.
 *
 * Distinct from /admin/accept-invite (one-time invite activation) —
 * this is for already-active staff who forgot their password.
 */
export default function AdminResetPassword() {
  const navigate = useNavigate();
  // Capture once, then strip the token from the address bar (history hygiene —
  // it's already POSTed in the body, so there's no backend leak). Refresh
  // afterward hits the "missing token" message below.
  // 🔑 hooks/useTokenFromLink: read once, stripped from the address bar, no-referrer while the page is open.
  const token = useTokenFromLink();
  const [pw, setPw]       = useState('');
  const [pwc, setPwc]     = useState('');
  const [showPw, setShowPw]   = useState(false);
  const [showPwc, setShowPwc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]   = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid reset link</h1>
          <p className="text-sm text-gray-500 mb-5">This link is missing its token. Please request a new one from the login page.</p>
          <Link to="/admin/login" className="inline-block px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: '#3a6ad9' }}>
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8 || !/\d/.test(pw) || !/[A-Z]/.test(pw)) {
      setError('Password must be at least 8 characters and include a number and an uppercase letter.');
      return;
    }
    if (pw !== pwc) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/reset-password`, {
        token,
        newPassword: pw,
        confirmPassword: pwc,
      });
      setDone(true);
      setTimeout(() => navigate('/admin/login', { replace: true }), 2000);
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setError(err.response?.data?.error || 'This reset link has expired or is invalid. Please request a new one.');
      } else {
        setError(err.response?.data?.error || 'Could not reset your password. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-bold mb-2" style={{ color: '#15803d' }}>✓ Password updated</h1>
          <p className="text-sm text-gray-500">Redirecting you to login…</p>
        </div>
      </div>
    );
  }

  const inputCls = 'w-full h-12 px-4 pr-16 rounded-xl border border-gray-200 text-sm text-gray-900 outline-none focus:ring-2 focus:border-transparent';
  const eyeBtn = {
    position: 'absolute', right: 12, top: 0, height: 48,
    background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 12, fontWeight: 600,
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div style={{ borderRadius: '16px', overflow: 'hidden', display: 'inline-block', marginBottom: '16px' }}>
            <img src="/doorly_logo_black.png" alt="Doorly" style={{ height: '36px', width: 'auto', display: 'block' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Reset Your Password</h1>
          <p className="text-gray-500 text-sm mt-1">Enter and confirm your new DoorlyDesk password.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {error && (
            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}
          <form onSubmit={submit} className="space-y-4" noValidate>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPw ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} required autoFocus
                  className={inputCls} style={{ '--tw-ring-color': '#3a6ad9' }} placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw((v) => !v)} style={eyeBtn}>
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPwc ? 'text' : 'password'} value={pwc} onChange={(e) => setPwc(e.target.value)} required
                  className={inputCls} style={{ '--tw-ring-color': '#3a6ad9' }} placeholder="••••••••" />
                <button type="button" onClick={() => setShowPwc((v) => !v)} style={eyeBtn}>
                  {showPwc ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-400">At least 8 characters, 1 uppercase letter, 1 number.</p>
            <button type="submit" disabled={submitting}
              className="w-full h-12 rounded-xl text-sm font-semibold text-white flex items-center justify-center transition-opacity"
              style={{ backgroundColor: '#3a6ad9', opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'Updating…' : 'Set New Password'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          <Link to="/admin/login" style={{ color: '#3a6ad9' }}>Back to login</Link>
        </p>
      </div>
    </div>
  );
}
